import { useQuery } from "@tanstack/react-query";
import { BrainCircuit, Download } from "lucide-react";
import { useMemo, useState } from "react";

import { apiClient } from "@/lib/api";
import { usePageTitle } from "@/lib/use-page-title";
import type { AiCostAuditParams } from "@tilt/admin-sdk";

type Tab = "summary" | "audit";

type Preset = "mtd" | "7d" | "30d" | "custom";
const PRESET_LABEL: Record<Preset, string> = {
  mtd: "Month to date",
  "7d": "Last 7 days",
  "30d": "Last 30 days",
  custom: "Custom",
};

function monthStart(): string {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
}

function daysAgo(n: number): string {
  return new Date(Date.now() - n * 86400_000).toISOString();
}

function fmtUsd(v: string | null | undefined): string {
  if (!v) return "-";
  const n = parseFloat(v);
  return isNaN(n) ? "-" : `$${n.toFixed(4)}`;
}

function fmtNum(v: number | null | undefined): string {
  if (v == null) return "-";
  return v.toLocaleString();
}

function fmtMs(v: number | null | undefined): string {
  if (v == null) return "-";
  return v >= 1000 ? `${(v / 1000).toFixed(1)}s` : `${v}ms`;
}

// ---------------------------------------------------------------- //
// Summary tab
// ---------------------------------------------------------------- //

function SummaryTab({
  since,
  until,
}: {
  readonly since: string;
  readonly until: string;
}): JSX.Element {
  const { data, isLoading, isError } = useQuery({
    queryKey: ["ai-costs", "summary", since, until],
    queryFn: () => apiClient.getAiCostSummary({ since, until }),
    retry: false,
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16 text-sm text-muted-foreground">
        Loading…
      </div>
    );
  }
  if (isError || !data) {
    return <p className="py-8 text-center text-sm text-destructive">Failed to load summary.</p>;
  }

  const totalUsd = parseFloat(data.total_cost_usd);
  const totalCalls = data.total_calls;

  return (
    <div className="space-y-6">
      {/* Headline totals */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
        <div className="rounded-lg border bg-card p-4 shadow-sm">
          <p className="text-xs text-muted-foreground">Total spend (period)</p>
          <p className="mt-1 text-2xl font-bold tabular-nums text-foreground">
            ${totalUsd.toFixed(4)}
          </p>
        </div>
        <div className="rounded-lg border bg-card p-4 shadow-sm">
          <p className="text-xs text-muted-foreground">Total LLM calls</p>
          <p className="mt-1 text-2xl font-bold tabular-nums text-foreground">
            {fmtNum(totalCalls)}
          </p>
        </div>
        <div className="rounded-lg border bg-card p-4 shadow-sm">
          <p className="text-xs text-muted-foreground">Partners using AI</p>
          <p className="mt-1 text-2xl font-bold tabular-nums text-foreground">
            {data.partners.length}
          </p>
        </div>
      </div>

      {/* Per-partner table */}
      {data.partners.length === 0 ? (
        <p className="text-center text-sm text-muted-foreground py-8">
          No AI calls in this window.
        </p>
      ) : (
        <div className="rounded-lg border bg-card shadow-sm overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/40 text-left text-xs font-medium uppercase tracking-wide text-muted-foreground">
                <th className="px-4 py-2.5">Partner</th>
                <th className="px-4 py-2.5 text-right">Calls</th>
                <th className="px-4 py-2.5 text-right">Prompt tokens</th>
                <th className="px-4 py-2.5 text-right">Completion tokens</th>
                <th className="px-4 py-2.5 text-right">Cost (USD)</th>
                <th className="px-4 py-2.5">Models</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {data.partners.map((p) => (
                <tr key={p.partner_id} className="hover:bg-muted/20">
                  <td className="px-4 py-2.5">
                    <p className="font-medium text-foreground">{p.partner_name ?? p.partner_id}</p>
                    {p.partner_path && (
                      <p className="text-xs text-muted-foreground font-mono">{p.partner_path}</p>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-right tabular-nums">{fmtNum(p.call_count)}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums">{fmtNum(p.prompt_tokens)}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums">
                    {fmtNum(p.completion_tokens)}
                  </td>
                  <td className="px-4 py-2.5 text-right tabular-nums font-semibold">
                    {fmtUsd(p.cost_usd)}
                  </td>
                  <td className="px-4 py-2.5">
                    <div className="flex flex-wrap gap-1">
                      {p.model_breakdown.map((mb) => (
                        <span
                          key={mb.model}
                          className="rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-mono text-muted-foreground"
                          title={`${mb.call_count} calls · ${fmtUsd(mb.cost_usd)}`}
                        >
                          {mb.model}
                        </span>
                      ))}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- //
// Audit tab
// ---------------------------------------------------------------- //

function AuditTab({
  since,
  until,
}: {
  readonly since: string;
  readonly until: string;
}): JSX.Element {
  const [cursor, setCursor] = useState<string | undefined>(undefined);

  const params = useMemo<AiCostAuditParams>(() => {
    const p: AiCostAuditParams = { limit: 50, since, until };
    if (cursor) p.cursor = cursor;
    return p;
  }, [since, until, cursor]);

  const { data, isLoading, isError } = useQuery({
    queryKey: ["ai-costs", "audit", params],
    queryFn: () => apiClient.listAiCostAudit(params),
    retry: false,
  });

  const exportCsv = (): void => {
    const items = data?.items ?? [];
    if (items.length === 0) return;
    const headers = [
      "created_at",
      "partner",
      "user",
      "recipe_kind",
      "provider",
      "model",
      "prompt_tokens",
      "completion_tokens",
      "cost_usd",
      "http_status",
      "duration_ms",
      "error_kind",
    ];
    const rows = items.map((r) => [
      r.created_at,
      r.partner_name ?? r.partner_id,
      r.user_display_name ?? "",
      r.recipe_kind,
      r.provider_slug,
      r.model,
      r.prompt_tokens ?? "",
      r.completion_tokens ?? "",
      r.cost_usd ?? "",
      r.http_status ?? "",
      r.duration_ms ?? "",
      r.error_kind ?? "",
    ]);
    const csv = [headers, ...rows]
      .map((row) => row.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const stamp = new Date().toISOString().slice(0, 19).replace(/:/g, "-");
    a.download = `ai-costs-${stamp}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <button
          type="button"
          onClick={exportCsv}
          disabled={(data?.items.length ?? 0) === 0}
          className="inline-flex items-center gap-2 rounded-md border bg-card px-3 py-1.5 text-sm hover:bg-muted disabled:opacity-40"
        >
          <Download className="h-4 w-4" />
          Export CSV
        </button>
      </div>

      {isLoading && (
        <div className="flex items-center justify-center py-16 text-sm text-muted-foreground">
          Loading…
        </div>
      )}
      {isError && (
        <p className="py-8 text-center text-sm text-destructive">Failed to load audit rows.</p>
      )}

      {!isLoading && !isError && (
        <>
          <div className="rounded-lg border bg-card shadow-sm overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/40 text-left text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  <th className="px-4 py-2.5">Time</th>
                  <th className="px-4 py-2.5">Partner</th>
                  <th className="px-4 py-2.5">User</th>
                  <th className="px-4 py-2.5">Recipe</th>
                  <th className="px-4 py-2.5">Model</th>
                  <th className="px-4 py-2.5 text-right">Tokens (P/C)</th>
                  <th className="px-4 py-2.5 text-right">Cost</th>
                  <th className="px-4 py-2.5 text-right">Latency</th>
                  <th className="px-4 py-2.5">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {(data?.items ?? []).map((r) => (
                  <tr key={r.id} className="hover:bg-muted/20">
                    <td className="px-4 py-2 text-xs text-muted-foreground tabular-nums whitespace-nowrap">
                      {new Date(r.created_at).toLocaleString()}
                    </td>
                    <td className="px-4 py-2 text-xs font-medium">
                      {r.partner_name ?? r.partner_id.slice(0, 8)}
                    </td>
                    <td className="px-4 py-2 text-xs text-muted-foreground">
                      {r.user_display_name ?? "-"}
                    </td>
                    <td className="px-4 py-2 font-mono text-xs">{r.recipe_kind}</td>
                    <td className="px-4 py-2 font-mono text-xs">{r.model}</td>
                    <td className="px-4 py-2 text-right tabular-nums text-xs">
                      {fmtNum(r.prompt_tokens)} / {fmtNum(r.completion_tokens)}
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums text-xs font-semibold">
                      {fmtUsd(r.cost_usd)}
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums text-xs">
                      {fmtMs(r.duration_ms)}
                    </td>
                    <td className="px-4 py-2 text-xs">
                      {r.error_kind ? (
                        <span className="rounded-full bg-destructive/10 px-1.5 py-0.5 text-destructive text-[10px] font-medium">
                          {r.error_kind}
                        </span>
                      ) : (
                        <span className="rounded-full bg-emerald-100 px-1.5 py-0.5 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300 text-[10px] font-medium">
                          {r.http_status ?? "ok"}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
                {(data?.items ?? []).length === 0 && (
                  <tr>
                    <td colSpan={9} className="py-10 text-center text-sm text-muted-foreground">
                      No rows in this window.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {(data?.next_cursor || cursor) && (
            <div className="flex items-center justify-between text-sm">
              <button
                type="button"
                onClick={() => setCursor(undefined)}
                disabled={!cursor}
                className="rounded-md border bg-card px-3 py-1.5 hover:bg-muted disabled:opacity-40"
              >
                ← First page
              </button>
              <button
                type="button"
                onClick={() => {
                  if (data?.next_cursor) setCursor(data.next_cursor);
                }}
                disabled={!data?.next_cursor}
                className="rounded-md border bg-card px-3 py-1.5 hover:bg-muted disabled:opacity-40"
              >
                Next page →
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- //
// Main page
// ---------------------------------------------------------------- //

export function AiCostsPage(): JSX.Element {
  usePageTitle("AI Costs");
  const [tab, setTab] = useState<Tab>("summary");
  const [preset, setPreset] = useState<Preset>("mtd");
  const [customSince, setCustomSince] = useState("");
  const [customUntil, setCustomUntil] = useState("");

  const { since, until } = useMemo(() => {
    if (preset === "custom") {
      return {
        since: customSince
          ? new Date(customSince).toISOString()
          : new Date(Date.now() - 30 * 86400_000).toISOString(),
        until: customUntil ? new Date(customUntil).toISOString() : new Date().toISOString(),
      };
    }
    return {
      since: preset === "mtd" ? monthStart() : daysAgo(preset === "7d" ? 7 : 30),
      until: new Date().toISOString(),
    };
  }, [preset, customSince, customUntil]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-start gap-3">
        <div className="flex items-center gap-2">
          <BrainCircuit className="h-5 w-5 text-indigo-500 shrink-0" aria-hidden="true" />
          <div>
            <h1 className="text-xl font-semibold tracking-tight sm:text-2xl">AI Costs</h1>
            <p className="text-sm text-muted-foreground">
              LLM spend and call audit across all partners.
            </p>
          </div>
        </div>
      </div>

      {/* Time range filter */}
      <div className="rounded-lg border bg-card p-3 shadow-sm sm:p-4 space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Window
          </span>
          {(["mtd", "7d", "30d", "custom"] as Preset[]).map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => setPreset(p)}
              className={
                preset === p
                  ? "rounded-md bg-primary px-2.5 py-1 text-xs font-medium text-primary-foreground"
                  : "rounded-md border bg-background px-2.5 py-1 text-xs text-muted-foreground hover:bg-muted hover:text-foreground"
              }
            >
              {PRESET_LABEL[p]}
            </button>
          ))}
        </div>

        {preset === "custom" && (
          <div className="flex flex-wrap items-center gap-3">
            <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
              From
              <input
                type="datetime-local"
                value={customSince}
                onChange={(e) => setCustomSince(e.target.value)}
                className="rounded-md border bg-background px-2 py-1 text-xs"
              />
            </label>
            <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
              To
              <input
                type="datetime-local"
                value={customUntil}
                onChange={(e) => setCustomUntil(e.target.value)}
                className="rounded-md border bg-background px-2 py-1 text-xs"
              />
            </label>
          </div>
        )}
      </div>

      {/* Tab bar */}
      <div className="flex gap-1 border-b">
        {(["summary", "audit"] as Tab[]).map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            className={
              tab === t
                ? "border-b-2 border-primary px-4 py-2 text-sm font-medium text-foreground"
                : "px-4 py-2 text-sm text-muted-foreground hover:text-foreground"
            }
          >
            {t === "summary" ? "Summary" : "Raw audit"}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {tab === "summary" ? (
        <SummaryTab since={since} until={until} />
      ) : (
        <AuditTab since={since} until={until} />
      )}
    </div>
  );
}
